import styled from "@emotion/styled";
import { Icon, LightMode, Text, Variables } from "@repo/skapa";
import isString from "lodash/isString";
import { type ReactElement } from "react";

import { FlexDiv } from "../FlexDiv";

export interface ListViewItemWithActionsProps {
  icon?: (...args: any[]) => any;
  title: string | ReactElement;
  description?: string | ReactElement;
  addOn?: string | ReactElement;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  inset?: boolean;
  disabled?: boolean;
  extraPadding?: boolean;
  action?: ReactElement;
}

const CustomListViewItemContainer = styled(FlexDiv)<{
  inset: boolean;
  disabled?: boolean;
  extraPadding?: boolean;
}>`
  cursor: pointer;
  padding: ${({ extraPadding }) =>
    extraPadding ? `${Variables.space150} 0;` : `${Variables.space75} 0;`};
  border-bottom: ${Variables.thicknessThin} solid ${LightMode.colourNeutral3};
  ${({ inset }) =>
    inset
      ? `padding-left: ${Variables.space150};padding-right: ${Variables.space150};`
      : ""}

  &:hover {
    .custom-list-view-item__title {
      ${({ disabled }) => (disabled ? " text-decoration: underline;" : "")}
    }
  }

  &&:focus-visible {
    outline-offset: -2px;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  gap: ${Variables.space50};
`;

const IconContainer = styled.div`
  width: ${Variables.space250};
`;

export const ListViewItemWithActions = ({
  icon,
  title,
  description,
  addOn,
  onClick,
  onMouseEnter,
  onMouseLeave,
  inset = false,
  disabled,
  extraPadding,
  action,
}: ListViewItemWithActionsProps) => {
  return (
    <CustomListViewItemContainer
      className="saleslead__element--focus-visible"
      tabIndex={disabled || !onClick ? undefined : 0}
      grow
      inset={inset}
      disabled={disabled}
      extraPadding={extraPadding}
      justifyContent="space-between"
      alignItems="center"
      gap={Variables.space100}
      onClick={disabled ? undefined : onClick}
      onKeyUp={(event) => {
        if (event.key === "Enter" || event.key === "Space") {
          if (disabled || !onClick) {
            return;
          }

          onClick();
        }
      }}
      onMouseEnter={disabled ? undefined : onMouseEnter}
      onMouseLeave={disabled ? undefined : onMouseLeave}
      width="100%"
      as="li"
    >
      <FlexDiv width="100%">
        <IconContainer>
          <Icon
            paths={icon}
            color={
              disabled
                ? LightMode.colourTextAndIcon3
                : LightMode.colourTextAndIcon1
            }
          />
        </IconContainer>
        <FlexDiv flexDirection="column" width="100%">
          {isString(title) ? (
            <Text
              textStyle="Heading.XS"
              className="custom-list-view-item__title"
              color={
                disabled
                  ? LightMode.colourTextAndIcon3
                  : LightMode.colourTextAndIcon1
              }
            >
              {title}
            </Text>
          ) : (
            title
          )}
          <Text
            pre
            color={
              disabled
                ? LightMode.colourTextAndIcon3
                : LightMode.colourTextAndIcon1
            }
          >
            {description}
          </Text>
          {addOn}
        </FlexDiv>
      </FlexDiv>
      {action && (
        <ActionsContainer
          onClick={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
          }}
          onKeyUp={(e) => {
            e?.stopPropagation();
          }}
        >
          {action}
        </ActionsContainer>
      )}
    </CustomListViewItemContainer>
  );
};
